import type { Invoice } from "src/types";

type NameFields = { firstName?: string; lastName?: string } | null | undefined;

export type OpenInvoiceSearchRow = {
    readableId: string;
    amount: number;
    recipientName?: string;
    patientName?: string;
};

function personSearchStrings(person: NameFields): string[] {
    if (!person) return [];
    const first = (person.firstName ?? "").trim();
    const last = (person.lastName ?? "").trim();
    const full = [first, last].filter(Boolean).join(" ");
    return [first, last, full].filter(Boolean).map((s) => s.toLowerCase());
}

function flatNameSearchStrings(name?: string): string[] {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    const parts = lower.split(/\s+/).filter(Boolean);
    return [lower, ...parts];
}

export function amountMatchesSearchQuery(amount: number | null | undefined, rawQuery: string): boolean {
    if (amount == null) return false;
    const q = rawQuery.trim().toLowerCase();
    if (!q) return false;

    const formatted = amount.toFixed(2);
    const formattedDe = formatted.replace(".", ",");

    const amountDigits = q.replace(/[€\s]/g, "").replace(",", ".");
    if (/^[\d.]+$/.test(amountDigits)) {
        if (formatted.includes(amountDigits) || formattedDe.includes(q.replace(/[€\s]/g, ""))) {
            return true;
        }
        const asNum = parseFloat(amountDigits);
        if (!Number.isNaN(asNum) && Math.abs(amount - asNum) < 0.005) {
            return true;
        }
    }

    return false;
}

/** True when the open-invoice bank-match search matches this row. */
export function openInvoiceMatchesSearch(invoice: OpenInvoiceSearchRow, rawQuery: string): boolean {
    const q = rawQuery.trim().toLowerCase();
    if (!q) return true;

    const nameParts = [
        ...flatNameSearchStrings(invoice.recipientName),
        ...flatNameSearchStrings(invoice.patientName),
    ];
    const haystack = [
        ...nameParts,
        (invoice.readableId ?? "").toLowerCase(),
    ].join(" ");

    if (haystack.includes(q) || nameParts.some((part) => part.includes(q))) {
        return true;
    }

    return amountMatchesSearchQuery(invoice.amount, rawQuery);
}

/** True when the invoice list search box query matches this row. */
export function invoiceMatchesSearch(invoice: Invoice, rawQuery: string): boolean {
    const q = rawQuery.trim().toLowerCase();
    if (!q) return true;

    const nameParts = [
        ...personSearchStrings(invoice.recipient),
        ...personSearchStrings(invoice.patient),
    ];
    const haystack = [
        ...nameParts,
        (invoice.doctor ?? "").toLowerCase(),
        (invoice.readableId ?? "").toLowerCase(),
    ].join(" ");

    if (haystack.includes(q) || nameParts.some((part) => part.includes(q))) {
        return true;
    }

    return amountMatchesSearchQuery(invoice.amount, rawQuery);
}
